'use client'

import { useState, useEffect } from 'react'
import s from './ContactForm.module.scss'
import { Input } from '../Input/Input'
import { Textarea } from '../Textarea'
import { Select, type SelectOption } from '../Select'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import { Checkbox } from '@/components/Checkbox'
import {
  SocialLinks,
  type SocialItem,
  type SocialName,
} from '@/components/SocialIcon'
import { useDict } from '@/lib'
import { basicData } from '@/constants/basicData'
import { trackSchoolContact } from '@/utils/trackSchoolContact'

// Interface for school data
export interface SchoolContactData {
  id: string
  name: string
  contact: {
    email?: string
    phone?: string
  }
  address?: string // Optional school address
  socials?: SocialItem[] // Optional school socials
  offerInfo?: {
    id: string
    title: string
    price: number
    licenseCategory?: string
  } // Optional offer information for inquiries
}

// Interface for general company contact data
export interface CompanyContactData {
  companyName: string
  address: string
  phone: {
    text: string
    href: string
  }
}

interface ContactFormProps {
  title?: string
  consentText?: string
  onSubmit?: (data: FormData) => void
  // School data for school contact
  school?: SchoolContactData
  // Company data for general contact (raw data from database)
  companyData?: Record<string, string> | null
  // Global socials data (optional, will be loaded if not provided)
  globalSocials?: Record<string, string> | null
  // Custom email for support/contact forms
  supportEmail?: string
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  message: string
  inquiryType?: 'general' | 'offer_request' | 'course_info'
}

// Inquiry type options for Select component
const INQUIRY_OPTIONS: SelectOption[] = [
  { value: 'offer_request', label: 'Zapytanie o ofertę' },
  { value: 'course_info', label: 'Informacje o kursach' },
  { value: 'general', label: 'Ogólne pytanie' },
] as const

export function ContactForm({
  title,
  consentText,
  onSubmit,
  school,
  companyData,
  globalSocials: propGlobalSocials,
  supportEmail,
}: ContactFormProps) {
  const [isChecked, setIsChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [globalSocials, setGlobalSocials] = useState<Record<
    string,
    string
  > | null>(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    inquiryType: 'offer_request',
  })

  const dict = useDict('contact-form')
  const modalDict = useDict('modal-contact')

  // Załaduj globalne dane tylko jeśli nie zostały przekazane jako prop
  useEffect(() => {
    if (propGlobalSocials) {
      setGlobalSocials(propGlobalSocials)
      setLoading(false)
      return
    }

    const loadGlobalData = async () => {
      try {
        const response = await fetch('/api/global-links')
        if (response.ok) {
          const data = await response.json()
          setGlobalSocials(data.socials)
        }
      } catch (error) {
        console.error('Error loading global data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadGlobalData()
  }, [propGlobalSocials])

  // Ustaw loading na false gdy companyData jest dostępne
  useEffect(() => {
    if (companyData) {
      setLoading(false)
    }
  }, [companyData])

  // Formatuj numer telefonu
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return ''

    // Jeśli już ma kierunkowy, formatuj z spacjami
    if (phone.startsWith('+')) {
      const digits = phone.replace(/\D/g, '')

      // Sprawdź długość kierunkowego i formatuj odpowiednio
      if (digits.startsWith('380')) {
        // Ukraina: +380 XX XXX XXXX
        return `+380 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(
          8,
        )}`
      } else if (digits.startsWith('421')) {
        // Słowacja: +421 XX XXX XXXX
        return `+421 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(
          8,
        )}`
      } else if (digits.startsWith('420')) {
        // Czechy: +420 XXX XXX XXX
        return `+420 ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(
          9,
        )}`
      } else if (digits.startsWith('49')) {
        // Niemcy: +49 XXX XXXXXXX
        return `+49 ${digits.slice(2, 5)} ${digits.slice(5)}`
      } else if (digits.startsWith('44')) {
        // UK: +44 XXX XXX XXXX
        return `+44 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(
          8,
        )}`
      } else if (digits.startsWith('1')) {
        // USA: +1 XXX XXX XXXX
        return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(
          7,
        )}`
      } else if (digits.startsWith('48')) {
        // Polska: +48 XXX XXX XXX
        return `+48 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(
          8,
        )}`
      } else {
        // Domyślne formatowanie dla innych krajów
        if (digits.length >= 10) {
          return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
            5,
            8,
          )} ${digits.slice(8)}`
        }
        return phone
      }
    }

    // Usuń wszystkie znaki niebędące cyframi
    const digits = phone.replace(/\D/g, '')

    // Jeśli zaczyna się od 48, usuń to
    const cleanDigits = digits.startsWith('48') ? digits.slice(2) : digits

    // Formatuj: +48 XXX XXX XXX (dodaj kierunkowy automatycznie)
    if (cleanDigits.length === 9) {
      return `+48 ${cleanDigits.slice(0, 3)} ${cleanDigits.slice(
        3,
        6,
      )} ${cleanDigits.slice(6)}`
    }

    // Fallback - dodaj +48
    return `+48 ${cleanDigits}`
  }

  // Dynamic contact data - school data takes precedence over company data
  const contactData = school
    ? {
        companyName: school.name,
        address: school.address || 'Adres niedostępny',
        // Phone is intentionally omitted for schools — fetched on demand via API
        // to prevent scrapers from harvesting phone numbers from HTML
        phone: school.contact.phone
          ? { text: 'Zadzwoń', href: '#' }
          : undefined,
        hasProtectedPhone: !!school.contact.phone,
      }
    : companyData &&
        (companyData.name || companyData.street || companyData.city)
      ? {
          companyName:
            companyData.name || modalDict?.companyName || basicData.companyName,
          addressLine1: companyData.street
            ? `ul. ${[companyData.street, companyData.buildingNumber].filter(Boolean).join(' ')}`
            : companyData.buildingNumber || '',
          addressLine2:
            companyData.postalCode && companyData.city
              ? `${companyData.postalCode} ${companyData.city}`
              : companyData.postalCode || companyData.city || '',
          phone:
            globalSocials?.phone && globalSocials.phone.trim() !== ''
              ? {
                  text: formatPhoneNumber(globalSocials.phone),
                  href: `tel:${globalSocials.phone.replace(/[^0-9+]/g, '')}`,
                }
              : undefined,
        }
      : {
          companyName: modalDict?.companyName || basicData.companyName,
          addressLine1: '',
          addressLine2: '',
          phone:
            globalSocials?.phone && globalSocials.phone.trim() !== ''
              ? {
                  text: formatPhoneNumber(globalSocials.phone),
                  href: `tel:${globalSocials.phone.replace(/[^0-9+]/g, '')}`,
                }
              : undefined,
        }

  // Fetch phone number on demand to protect it from scrapers
  const handlePhoneClick = async () => {
    if (!school) return
    setPhoneLoading(true)
    try {
      const res = await fetch(`/api/driving-schools/${school.id}/phone`)
      if (!res.ok) return
      const data = await res.json()
      if (data.phone) {
        trackSchoolContact({
          schoolId: school.id,
          eventType: 'usedCallOnPhone',
        })
        window.location.href = `tel:${data.phone}`
      }
    } catch {
      // silently fail
    } finally {
      setPhoneLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isChecked) {
      setError('Musisz wyrazić zgodę na przetwarzanie danych osobowych.')
      return
    }

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.message
    ) {
      setError(dict?.emailValidation || 'Wypełnij wszystkie wymagane pola.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      if (school) {
        // School contact
        await handleSchoolContact()
      } else {
        // General contact
        await handleGeneralContact()
      }

      setSuccess(true)
      if (onSubmit) {
        onSubmit(formData)
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Wystąpił błąd podczas wysyłania wiadomości.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSchoolContact = async () => {
    if (!school) return

    // Check if school has email - if not, send to zebra email with special note
    const hasSchoolEmail =
      school.contact.email && school.contact.email.trim() !== ''
    const targetEmail = hasSchoolEmail
      ? school.contact.email
      : 'wyszukiwarka-osk@zebra.to'

    // Add special note for schools without email
    let messageWithNote = hasSchoolEmail
      ? formData.message
      : `${formData.message}\n\n--- WYSŁANO DO SZKOŁY BEZ ADRESU ---`

    // Add offer information if available
    if (school.offerInfo) {
      const offerDetails = `\n\n--- ZAPYTANIE O OFERTĘ ---\nNazwa oferty: ${
        school.offerInfo.title
      }\nCena: ${school.offerInfo.price} zł${
        school.offerInfo.licenseCategory
          ? `\nKategoria prawa jazdy: ${school.offerInfo.licenseCategory}`
          : ''
      }`
      messageWithNote += offerDetails
    }

    const response = await fetch('/api/send-school-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        senderName: `${formData.firstName} ${formData.lastName}`,
        senderEmail: formData.email,
        senderPhone: formData.phone,
        message: messageWithNote,
        schoolName: school.name,
        schoolContact: {
          email: targetEmail,
          phone: school.contact.phone,
        },
        inquiryType: formData.inquiryType,
        preferredContact: 'email',
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(
        data.error || 'Wystąpił błąd podczas wysyłania wiadomości.',
      )
    }

    trackSchoolContact({ schoolId: school.id, eventType: 'usedContactForm' })
  }

  const handleGeneralContact = async () => {
    // For general contact, we could implement a general contact API
    // For now, we'll use the school contact API with company data

    const response = await fetch('/api/send-school-contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        senderName: `${formData.firstName} ${formData.lastName}`,
        senderEmail: formData.email,
        senderPhone: formData.phone,
        message: formData.message,
        schoolName: contactData.companyName,
        schoolContact: {
          email: supportEmail || 'kontakt@zebra.to', // Use supportEmail if provided, otherwise default
          phone: contactData.phone?.text || '',
        },
        inquiryType: 'general',
        preferredContact: 'email',
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(
        data.error || 'Wystąpił błąd podczas wysyłania wiadomości.',
      )
    }
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError(null)
  }

  if (!dict || !modalDict) return null

  // Success state - use original simple success without custom styling
  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p className={s.successMessage}>Wiadomość została wysłana!</p>
        {school && (
          <p className={s.contactSoonMessage}>
            Szkoła skontaktuje się z Tobą wkrótce.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={s.contactForm}>
      {/* Contact Info Section - ORYGINALNY UKŁAD */}
      <div className={s.contactInfo}>
        <div className={s.leftColumn}>
          <p>{contactData.companyName}</p>
          {contactData.addressLine1 &&
            contactData.addressLine1.trim() !== '' && (
              <p>{contactData.addressLine1}</p>
            )}
          {contactData.addressLine2 &&
            contactData.addressLine2.trim() !== '' && (
              <p>{contactData.addressLine2}</p>
            )}
          {contactData.address && !contactData.addressLine1 && (
            <p>{contactData.address}</p>
          )}
        </div>
        {(school ? contactData.hasProtectedPhone : contactData.phone) && (
          <div className={s.centerColumn}>
            {school ? (
              <button
                type="button"
                className={s.phoneLink}
                onClick={handlePhoneClick}
                disabled={phoneLoading}
              >
                {phoneLoading ? 'Łączenie...' : 'Zadzwoń'}
              </button>
            ) : (
              contactData.phone && (
                <a href={contactData.phone.href} className={s.phoneLink}>
                  {contactData.phone.text}
                </a>
              )
            )}
          </div>
        )}
        {(!school ||
          (school.socials &&
            Object.keys(school.socials).some(
              (key) => school.socials?.[key as keyof typeof school.socials],
            ))) && (
          <div className={s.rightColumn}>
            {!school && (
              <div className={s.socials}>
                <SocialLinks
                  socials={['tiktok', 'instagram', 'facebook', 'threads']}
                  useGlobalSocials={true}
                  globalSocials={globalSocials}
                />
              </div>
            )}
            {school &&
              school.socials &&
              Object.keys(school.socials).some(
                (key) => school.socials?.[key as keyof typeof school.socials],
              ) && (
                <div className={s.socials}>
                  <SocialLinks
                    socials={Object.entries(school.socials)
                      .filter(
                        ([key, value]) =>
                          value &&
                          typeof value === 'string' &&
                          value.trim() !== '',
                      )
                      .map(([key]) => key as any)}
                    variant="custom"
                    customLinks={
                      school.socials as Partial<Record<SocialName, string>>
                    }
                    onLinkClick={() =>
                      trackSchoolContact({
                        schoolId: school.id,
                        eventType: 'usedSocialMedia',
                      })
                    }
                  />
                </div>
              )}
          </div>
        )}
      </div>

      <h2 className={s.formTitle}>{title || dict.formTitle}</h2>

      <form className={s.form} onSubmit={handleSubmit}>
        <div className={s.row}>
          <Input
            type="text"
            placeholder={dict.firstName}
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            autoComplete="given-name"
            name="firstName"
            required
          />
          <Input
            type="text"
            placeholder={dict.lastName}
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            autoComplete="family-name"
            name="lastName"
            required
          />
        </div>
        <div className={s.row}>
          <Input
            type="email"
            placeholder={dict.email}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            autoComplete="email"
            name="email"
            pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
            title={dict.emailValidation}
            required
          />
          <Input
            type="tel"
            placeholder={dict.phone}
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            autoComplete="tel"
            name="phone"
            pattern="[\+]?[0-9\s\-\(\)]{9,15}"
            title={dict.phoneValidation}
          />
        </div>

        {/* TYLKO dla szkół - typ zapytania */}
        {school && (
          <Select
            value={formData.inquiryType}
            onChange={(value) => handleInputChange('inquiryType', value)}
            options={INQUIRY_OPTIONS}
            placeholder="Wybierz typ zapytania"
          />
        )}

        <Textarea
          placeholder={dict.message}
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          name="message"
          required
        />

        {/* Błędy - prosty display */}
        {error && (
          <div
            style={{
              padding: '10px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '5px',
              color: '#c33',
              fontSize: '14px',
              marginBottom: '15px',
            }}
          >
            {error}
          </div>
        )}

        <div className={s.checkboxWrapper}>
          <Checkbox
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
          />
          <span>
            {school
              ? `Chcę się skontaktować z ${school.name} oraz wyrażam zgodę na przetwarzanie moich danych osobowych w tym celu.`
              : consentText || dict.consent}
          </span>
        </div>

        <ButtonOrLink
          as="button"
          type="submit"
          variant="primary"
          iconLeft="SendIcon"
          disabled={!isChecked || isLoading}
          href={undefined as never}
          target={undefined as never}
          rel={undefined as never}
        >
          {isLoading ? 'Wysyłanie...' : dict.send}
        </ButtonOrLink>
      </form>
    </div>
  )
}
